"use client";

import {parsePhoneNumber} from "awesome-phonenumber";
import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {humanizeToolField, isPresentationRecord, presentedFields, presentationText, type PresentedField, type ToolPresentation} from "@repo/ui/gailgpt/tool-presentation";
import Icon from "@/components/library/Icon";
import FrozenValue from "./FrozenValue";
import {occurrenceKeys, valueKey} from "./snapshotKeys";

function PresentedValue({field}: {field: PresentedField}) {
   const {t} = useTranslation("ask");
   const choice = field.choices?.find(choice => Object.is(choice.value, field.value));
   if (choice) return <>{presentationText(choice.label, t)}</>;
   if (field.value === "") return <FrozenValue value={field.value} humanizeLabels />;
   if (field.format === "entity" && field.entityName && typeof field.value === "string") return <span title={field.value}>{field.entityName}</span>;
   if (field.format === "phone" && typeof field.value === "string") {
      const phone = parsePhoneNumber(field.value);
      return <span dir="ltr">{phone.valid ? phone.number.international : field.value}</span>;
   }
   // Dates keep their explicit timezone; no browser-local conversion can change the review.
   if (field.format === "date-time" && typeof field.value === "string") return <time dateTime={field.value}>{field.value}</time>;
   return <FrozenValue value={field.value} humanizeLabels />;
}

export const disclosureClass = "flex cursor-pointer list-none items-center gap-2 rounded text-xs text-500 hover:text-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary [&::-webkit-details-marker]:hidden";

export function CollectionReview({field, initiallyOpen = true, preview, columnLabel, renderCell}: {
   field: PresentedField & {value: unknown[]};
   initiallyOpen?: boolean;
   preview?: ReactNode;
   columnLabel?: (column: string) => ReactNode;
   renderCell?: (record: Record<string, unknown>, column: string) => ReactNode;
}) {
   const {t} = useTranslation("ask");
   const records = field.value.every(isPresentationRecord) ? field.value as Record<string, unknown>[] : null;
   const columns = records ? [...new Set(records.flatMap(item => Object.keys(item)))].filter(column => column !== "textScriptId") : [];
   const tabular = records && columns.length > 0 && columns.length <= 5 && records.every(item => Object.values(item).every(value => value === null || typeof value !== "object"));
   return <details open={initiallyOpen} className="group/collection rounded-lg border border-neutral">
      <summary className={`${disclosureClass} px-3 py-3 text-sm`}>
         <Icon icon="chevron-right" className="h-2.5 w-2.5 shrink-0 transition-transform group-open/collection:rotate-90" />
         <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
            <span className="font-medium text-700">{presentationText(field.label, t)}</span>
            {preview && <span className="mt-1 block text-xs leading-relaxed text-500 group-open/collection:hidden">
               {preview}
            </span>}
         </span>
         <span className="rounded bg-100 px-2 py-0.5 text-xs tabular-nums text-600">{field.value.length}</span>
      </summary>
      <div className="border-t border-neutral p-3">
         {tabular ? <table className="w-full text-left text-xs">
            <thead><tr>{columns.map(column => <th key={column} scope="col" className="px-2 pb-2 font-medium text-500">{columnLabel?.(column) ?? humanizeToolField(column)}</th>)}</tr></thead>
            <tbody>{occurrenceKeys(records, valueKey).map(({item: record, key}) => <tr key={key} className="border-t border-neutral">
               {columns.map(column => <td key={column} className="px-2 py-2 align-top text-700 [overflow-wrap:anywhere]">{renderCell?.(record, column) ?? <FrozenValue value={record[column]} field={column} humanizeLabels />}</td>)}
            </tr>)}</tbody>
         </table> : <FrozenValue value={field.value} humanizeLabels />}
      </div>
   </details>;
}

function FieldReview({field, compact = false}: {field: PresentedField; compact?: boolean}) {
   const {t} = useTranslation("ask");
   const primary = field.emphasis === "primary";
   const multiline = field.format === "multiline";
   let valueClass = "text-700";
   if (primary) valueClass = "text-lg font-semibold leading-snug tracking-tight text-900";
   else if (multiline) valueClass = "leading-relaxed text-700";
   let layoutClass = compact ? "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-4" : "grid grid-cols-[minmax(0,30%)_minmax(0,1fr)] gap-x-4";
   if (primary || multiline) layoutClass = "space-y-1.5";
   return <div className={layoutClass}>
      <dt className={`text-500 [overflow-wrap:anywhere] ${primary || multiline ? "mb-1 text-xs font-medium" : ""}`}>{presentationText(field.label, t)}</dt>
      <dd className={`[overflow-wrap:anywhere] ${valueClass}`}>
         <PresentedValue field={field} />
      </dd>
   </div>;
}

/** Resource renderers can supply a field body without teaching the fallback about that resource. */
export function ReviewFields({fields, compact = false, renderField}: {
   fields: PresentedField[];
   compact?: boolean;
   renderField?: (field: PresentedField) => ReactNode;
}) {
   const {t} = useTranslation("ask");
   return <dl className={compact ? "space-y-3 text-sm" : "space-y-5 text-sm"}>
      {fields.map(field => {
         const custom = renderField?.(field);
         const collection = Array.isArray(field.value) && field.value.length > 3
            ? <CollectionReview field={{...field, value: field.value}} /> : undefined;
         const content = custom ?? collection;
         return content !== undefined
            ? <div className="relative" key={JSON.stringify(field.path)}><dt className="sr-only">{presentationText(field.label, t)}</dt><dd>{content}</dd></div>
            : <FieldReview key={JSON.stringify(field.path)} field={field} compact={compact} />;
      })}
   </dl>;
}

export type ToolDetailsProps = {presentation: ToolPresentation; parameters: Record<string, unknown>};

/** Field values always come from the bound request. Metadata only controls their presentation. */
export default function ToolPresentationDetails({presentation, parameters}: ToolDetailsProps) {
   const {t} = useTranslation("ask");
   const fields = presentedFields(parameters, presentation.fields);
   return <div className="px-5 pb-5 pt-4">
      <ReviewFields fields={fields} />
      {!fields.length && <p className="text-sm text-500">{t("toolApproval.execution.noParameters")}</p>}
   </div>;
}
